/**
 * One-shot hydration of the entire state store from /api/state/bootstrap.
 *
 * Phase D — fires once on cold load BEFORE WS state_init arrives. Eliminates
 * the "12 separate XHR" startup spike + the brief skeleton-flash period.
 *
 * Usage from App.tsx (after auth, before main render):
 *   useEffect(() => { bootstrapState(); }, []);
 *
 * Subsequent state changes flow via WS state_init/state_delta — this helper
 * is fire-and-forget; failures are logged but don't block the UI (the WS
 * path will populate the store within a few seconds either way).
 */
import { useStateStore, type DomainKey } from "@/state/store";
import { mirrorDomainToQueryCache } from "@/state/queryCacheMirror";

export interface BootstrapResponse {
  as_of:        string | null;
  domain_count: number;
  domains:      Record<string, unknown>;
}

const KNOWN_DOMAINS: DomainKey[] = [
  "portfolio", "positions", "orders", "trades", "signals",
  "indices", "system_health", "auth_status", "alerts",
  "settings_meta", "gtt", "scheduler_status", "metrics",
  "feature_flags", "dispatcher",
  // 2026-04-27 — wire holdings + charges through bootstrap
  "holdings", "charges",
  // 2026-04-27 Phase H — bundle of 4 status toggles
  "system_toggles",
  // 2026-04-27 Phase I — bundle of pnl-by-index + lifecycles + bn_directional
  "system_extras",
];

/** Fetch + hydrate. Returns true on success, false on any failure.
 *
 * 2026-04-24: also primes React Query's cache with the bootstrap data so
 * that `useQuery` calls in components find data already cached and skip
 * the per-component REST call. This is the actual "one call instead of
 * 30 on reload" fix — works WITHOUT migrating individual components to
 * useStateStore hooks.
 *
 * 2026-04-27: debounced. WS reconnect storms (visible in prod as
 * /api/state/bootstrap firing every 3s) caused this to fire once per
 * reconnect. Now coalesces calls within a 30s window — second+ calls
 * wait on the in-flight promise (or skip if a recent one already
 * succeeded). Eliminates the "bootstrap pile-up" observed in logs.
 */
let _lastBootstrapTs = 0;
let _inflight: Promise<boolean> | null = null;
const BOOTSTRAP_MIN_INTERVAL_MS = 30_000;

export async function bootstrapState(): Promise<boolean> {
  const now = Date.now();
  // Coalesce: if a call is in-flight, await its result (don't fire another)
  if (_inflight) return _inflight;
  // Debounce: skip if a successful call landed within the last 30s
  if (now - _lastBootstrapTs < BOOTSTRAP_MIN_INTERVAL_MS) {
    return true;  // pretend success — recent data is in the store
  }
  _inflight = _doBootstrap().finally(() => {
    _inflight = null;
  });
  return _inflight;
}

async function _doBootstrap(): Promise<boolean> {
  try {
    const token = localStorage.getItem("mct_token");
    const res = await fetch("/api/state/bootstrap", {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      // 404 is expected if STATE_AGGREGATOR_ENABLED=false on backend (legacy)
      if (res.status !== 404) {
        console.warn("[state-bootstrap] non-OK response:", res.status);
      }
      return false;
    }
    const body = (await res.json()) as BootstrapResponse;
    if (!body.domains) return false;

    // Apply to the Zustand state-store + mirror into React Query cache
    // in one pass. mirrorDomainToQueryCache is the single source of truth
    // for the domain → queryKey mapping (also used by the WS state_delta
    // handler so per-push cache writes match the bootstrap shape exactly).
    const applyInit = useStateStore.getState().applyInit;
    for (const key of KNOWN_DOMAINS) {
      const snap = body.domains[key];
      if (snap && typeof snap === "object") {
        const seq = ((snap as Record<string, unknown>)._seq as number) ?? 1;
        applyInit(key, snap as Record<string, unknown>, seq);
        try {
          mirrorDomainToQueryCache(key, snap as Record<string, unknown>);
        } catch (e) {
          console.warn(`[state-bootstrap] mirror ${key} failed:`, e);
        }
      }
    }

    _lastBootstrapTs = Date.now();
    return true;
  } catch (e) {
    console.warn("[state-bootstrap] failed:", e);
    return false;
  }
}
